import store, { useAppSelector } from 'app/store'
import useListAction from 'core/hooks/useListAction'
import useUpdateAction from 'core/hooks/useUpdateAction'
import { sessionActions, SessionState, sessionStoreKey } from 'core/session/sessionReducers'
import DataKeys from 'k8s/DataKeys'
import { getCurrentUser } from 'openstack/helpers'
import { prop } from 'ramda'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  listAllStatewiseVirtualMachinesLite,
  listAllVirtualMachines,
  listAllVirtualMachinesLite,
  listAllVirtualMachinesNextPage,
  listSelfStatewiseVirtualMachinesLite,
  listSelfVirtualMachines,
  listSelfVirtualMachinesLite,
  listSelfVirtualMachinesNextPage,
  listStatewiseVirtualMachinesLite,
  listVirtualMachines,
  listVirtualMachinesLite,
  listVirtualMachinesNextPage,
  pollAllVirtualMachines,
  pollSelfVirtualMachines,
  pollVirtualMachines,
  searchAllVirtualMachines,
  searchSelfVirtualMachines,
  searchVirtualMachines,
} from './actions'
import {
  allStatewiseVirtualMachinesLiteSelector,
  allVirtualMachinesLiteSelector,
  allVirtualMachinesSelector,
  selfStatewiseVirtualMachinesLiteSelector,
  selfVirtualMachinesLiteSelector,
  selfVirtualMachinesSelector,
  statewiseVirtualMachinesLiteSelector,
  virtualMachinesLiteSelector,
  virtualMachinesSelector,
} from './selectors'
import { VMFilterParams } from './VMServerSideSearch'

interface UseVirtualMachinesPaginationConfig {
  // Optional host parameter - when provided, it will be included in all API calls
  host?: string
  // Page size from session or default
  pageSize: number
  // User context flags
  allTenants: boolean
  allOwners: boolean
  // Session key for pagination (different for VM list vs Host VM list)
  sessionKey: 'vmList' | 'hostList'
  sessionSearchParams: string
  sessionSortKey: string
  sessionSortDir: string
  sessionPageSize: string
  sessionVMCachedParams: string
  sessionAllVMCachedParams: string
  sessionSelfVMCachedParams: string
  defaultSearchParams: VMFilterParams
}

/**
 * Custom hook that encapsulates all common VM list logic
 * Used by both VirtualMachinesListPage and HostVmsListPage
 *
 * The key difference: HostVmsListPage passes a `host` parameter
 * which gets included in all API calls
 *
 * @responsibility
 * - Handles pagination for VM list
 * - Manages search and sorting
 * - Manages session state
 * - Manages polling
 * - Manages statewise VM count
 *
 * @param config - Configuration object
 * @returns {Object} - Object containing pagination state and actions
 */
export function useVirtualMachinesPagination(config: UseVirtualMachinesPaginationConfig) {
  const {
    host,
    pageSize: initialPageSize,
    allTenants,
    allOwners,
    sessionKey,
    sessionSearchParams,
    sessionSortKey,
    sessionSortDir,
    sessionPageSize,
    sessionVMCachedParams,
    sessionAllVMCachedParams,
    sessionSelfVMCachedParams,
    defaultSearchParams,
  } = config

  const dispatch = useDispatch()
  const session = useSelector(prop<string, SessionState>(sessionStoreKey))
  const { role: userRole } = getCurrentUser()

  // Pagination state
  const [currentPage, setCurrentPage] = useState(0)
  const [pageMarkers, setPageMarkers] = useState<(string | null)[]>([null])
  const [isPaginationLoading, setIsPaginationLoading] = useState(false)
  const [pageSize, setPageSize] = useState(initialPageSize)
  const [sortKey, setSortKey] = useState<string>(session?.[sessionSortKey] || 'created_at')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>(session?.[sessionSortDir] || 'desc')

  // Search state - conditionally include host
  const cachedSearchParams = session?.[sessionSearchParams] ?? null
  const [searchParams, setSearchParams] = useState<Record<string, string> | null>(
    host ? { ...(cachedSearchParams || {}), host } : cachedSearchParams,
  )
  const [isSearchActive, setIsSearchActive] = useState(!!host)

  // Refs for lifecycle management
  const isInitialMount = useRef(true)
  const hasInitialDataLoaded = useRef(false)

  // Build params helper - always include host if provided
  const buildParams = useCallback(
    (additionalParams: any = {}) => {
      const baseParams = {
        limit: pageSize,
        sortKey,
        sortDir,
        ...additionalParams,
      }

      // If host is provided, merge it into searchParams
      if (host) {
        return {
          ...baseParams,
          searchParams: { ...(baseParams.searchParams || {}), host },
        }
      }

      return baseParams
    },
    [host, pageSize, sortKey, sortDir],
  )

  // ========== List Actions (Regular) ==========
  const { message, loading, reload, refreshing: vmRefreshing } = useListAction(
    listVirtualMachines,
    {
      loadOnDemand: true,
      params: buildParams({ ...(searchParams && { searchParams }) }),
    },
  )
  const data = useAppSelector(virtualMachinesSelector)

  // ========== List Actions (All Tenants) ==========
  const {
    message: allMessage,
    loading: allLoading,
    reload: allReload,
    refreshing: allVmRefreshing,
  } = useListAction(listAllVirtualMachines, {
    loadOnDemand: true,
    params: buildParams({ ...(searchParams && { searchParams }) }),
  })
  const allData = useAppSelector(allVirtualMachinesSelector)

  // ========== List Actions (Self/Member) ==========
  const {
    message: selfMessage,
    loading: selfLoading,
    reload: selfReload,
    refreshing: selfVmRefreshing,
  } = useListAction(listSelfVirtualMachines, {
    loadOnDemand: true,
    params: buildParams({ ...(searchParams && { searchParams }) }),
  })
  const selfData = useAppSelector(selfVirtualMachinesSelector)

  // ========== Lite Data for Counts ==========
  const { reload: reloadVmLite } = useListAction(listVirtualMachinesLite, {
    loadOnDemand: true,
    params: host ? { host } : {},
  })
  const { reload: reloadAllVmLite } = useListAction(listAllVirtualMachinesLite, {
    loadOnDemand: true,
    params: host ? { host } : {},
  })
  const { reload: reloadSelfVmLite } = useListAction(listSelfVirtualMachinesLite, {
    loadOnDemand: true,
    params: host ? { host } : {},
  })

  // ========== Statewise VM Count ==========
  const { reload: statewiseReload, loading: statewiseLoading } = useListAction(
    listStatewiseVirtualMachinesLite,
    {
      loadOnDemand: true,
      params: host ? { host } : {},
    },
  )
  const { reload: allStatewiseReload, loading: allStatewiseLoading } = useListAction(
    listAllStatewiseVirtualMachinesLite,
    {
      loadOnDemand: true,
      params: host ? { host } : {},
    },
  )
  const { reload: selfStatewiseReload, loading: selfStatewiseLoading } = useListAction(
    listSelfStatewiseVirtualMachinesLite,
    {
      loadOnDemand: true,
      params: host ? { host } : {},
    },
  )

  const vmStateCount = useAppSelector(statewiseVirtualMachinesLiteSelector)
  const allVmStateCount = useAppSelector(allStatewiseVirtualMachinesLiteSelector)
  const selfVmStateCount = useAppSelector(selfStatewiseVirtualMachinesLiteSelector)

  // Get lite data selectors
  const regularLiteData = useAppSelector(virtualMachinesLiteSelector)
  const allTenantsLiteData = useAppSelector(allVirtualMachinesLiteSelector)
  const selfLiteData = useAppSelector(selfVirtualMachinesLiteSelector)

  // ========== Polling ==========
  const { update: pollVms, updating: pollingVms } = useUpdateAction(pollVirtualMachines)
  const { update: pollAllVms, updating: pollingAllVms } = useUpdateAction(pollAllVirtualMachines)
  const { update: pollSelfVms, updating: pollingSelfVms } = useUpdateAction(pollSelfVirtualMachines)

  // ========== Helper Functions (Must be declared before use) ==========
  // Generic helper to select appropriate value based on user context
  const selectByContext = useCallback(
    <T>(allTenantsValue: T, selfValue: T, regularValue: T): T => {
      if (allTenants) return allTenantsValue
      if (userRole === 'member' && !allOwners) return selfValue
      return regularValue
    },
    [allTenants, userRole, allOwners],
  )

  // Helper to check if statewise data needs reload
  const shouldReloadStatewiseVirtualMachines = useCallback(
    (data: { success: number; warning: number; error: number }) => {
      return data?.success === 0 && data?.warning === 0 && data?.error === 0
    },
    [],
  )

  // Helper to get appropriate search action based on user context
  const getSearchAction = useCallback(() => {
    if (allTenants) return searchAllVirtualMachines
    if (userRole === 'member' && !allOwners) return searchSelfVirtualMachines
    return searchVirtualMachines
  }, [allTenants, userRole, allOwners])

  // Helper to get appropriate nextPage action based on user context
  const getNextPageAction = useCallback(() => {
    if (allTenants) return listAllVirtualMachinesNextPage
    if (userRole === 'member' && !allOwners) return listSelfVirtualMachinesNextPage
    return listVirtualMachinesNextPage
  }, [allTenants, userRole, allOwners])

  // Helper to get appropriate reload actions based on user context
  const getReloadActions = useCallback(() => {
    if (allTenants) {
      return {
        liteData: allTenantsLiteData,
        stateCount: allVmStateCount,
        reloadLite: reloadAllVmLite,
        reloadStatewise: allStatewiseReload,
      }
    }
    if (userRole === 'member' && !allOwners) {
      return {
        liteData: selfLiteData,
        stateCount: selfVmStateCount,
        reloadLite: reloadSelfVmLite,
        reloadStatewise: selfStatewiseReload,
      }
    }
    return {
      liteData: regularLiteData,
      stateCount: vmStateCount,
      reloadLite: reloadVmLite,
      reloadStatewise: statewiseReload,
    }
  }, [
    allTenants,
    userRole,
    allOwners,
    allTenantsLiteData,
    allVmStateCount,
    reloadAllVmLite,
    allStatewiseReload,
    selfLiteData,
    selfVmStateCount,
    reloadSelfVmLite,
    selfStatewiseReload,
    regularLiteData,
    vmStateCount,
    reloadVmLite,
    statewiseReload,
  ])

  // Common loading function used by both initial load and toggle effects
  const loadVMsData = useCallback(
    async ({ shouldReloadVmCounts = false } = {}) => {
      setIsPaginationLoading(true)
      try {
        const params = buildParams({ searchParams })
        const searchAction = getSearchAction()
        const { liteData, stateCount, reloadLite, reloadStatewise } = getReloadActions()

        const promises = []
        // Execute search
        promises.push(searchAction.call(params))

        // Conditionally reload lite data if empty
        if (liteData?.length === 0) {
          promises.push(reloadLite(true))
        }

        // Conditionally reload statewise data if all counts are zero
        if (shouldReloadStatewiseVirtualMachines(stateCount) || shouldReloadVmCounts) {
          promises.push(reloadStatewise(true))
        }

        // Wait for all promises to resolve
        await Promise.all(promises)
      } finally {
        setIsPaginationLoading(false)
      }
    },
    [
      buildParams,
      searchParams,
      getSearchAction,
      getReloadActions,
      shouldReloadStatewiseVirtualMachines,
    ],
  )

  // ========== Computed Values ==========
  const filteredData = useMemo(() => selectByContext(allData, selfData, data), [
    selectByContext,
    allData,
    selfData,
    data,
  ])

  // Total count of VMs
  const totalCount = useMemo(() => {
    // If search is active, use totalCount from session pagination (search results count)
    if (isSearchActive) {
      const paginationData = selectByContext(
        session?.allVmsPagination,
        session?.selfVmsPagination,
        session?.vmsPagination,
      )
      if (paginationData?.totalCount != null) {
        return paginationData.totalCount
      }
    }

    // Otherwise use lite API data
    const liteData = selectByContext(allTenantsLiteData, selfLiteData, regularLiteData)
    return Array.isArray(liteData) ? liteData?.length : undefined
  }, [selectByContext, regularLiteData, allTenantsLiteData, selfLiteData, isSearchActive, session])

  // Next page availability
  const hasNextPage = useMemo(() => {
    // Check if we have a marker for the next page
    const nextPageMarker = pageMarkers[currentPage + 1]
    if (nextPageMarker) {
      return true
    }

    // Check if based on total count we should have more pages
    if (totalCount) {
      const estimatedMaxPage = Math.ceil(totalCount / pageSize) - 1
      return currentPage < estimatedMaxPage
    }

    return false
  }, [currentPage, pageMarkers, totalCount, pageSize])

  // Paginated data
  const paginatedVMs = useMemo(() => {
    if (!filteredData || !Array.isArray(filteredData)) {
      return []
    }
    const startIndex = currentPage * pageSize
    const endIndex = startIndex + pageSize
    return filteredData.slice(startIndex, endIndex)
  }, [filteredData, currentPage, pageSize])

  // Loading state
  const activeLoading = useMemo(() => {
    // Show loading during pagination
    if (isPaginationLoading) return true
    return selectByContext(allLoading, selfLoading, loading)
  }, [isPaginationLoading, selectByContext, allLoading, selfLoading, loading])

  // Polling state
  const activePolling = useMemo(() => selectByContext(pollingAllVms, pollingSelfVms, pollingVms), [
    selectByContext,
    pollingAllVms,
    pollingSelfVms,
    pollingVms,
  ])

  // Message state
  const activeMessage = useMemo(() => selectByContext(allMessage, selfMessage, message), [
    selectByContext,
    allMessage,
    selfMessage,
    message,
  ])

  // Refreshing state
  const activeRefreshing = useMemo(
    () => selectByContext(allVmRefreshing, selfVmRefreshing, vmRefreshing),
    [selectByContext, allVmRefreshing, selfVmRefreshing, vmRefreshing],
  )

  // ========== Callbacks ==========
  const handlePageChange = useCallback(
    async (newPage: number, marker?: string) => {
      // Check if going backward or if we already have data for this page
      const isGoingBackward = newPage < currentPage
      const alreadyHasMarker = pageMarkers[newPage] !== undefined

      // Check if cache has enough data for the requested page
      const currentCachedData = selectByContext(allData, selfData, data)
      const cacheLength = currentCachedData?.length || 0

      // Calculate what slice we'd get for this page
      const startIndex = newPage * pageSize
      const endIndex = startIndex + pageSize

      // Skip API call if cache has data or going backward
      const cacheHasEnoughData = cacheLength >= endIndex
      const cacheHasAllData = totalCount != null && cacheLength >= totalCount

      // Update page immediately for instant navigation
      setCurrentPage(newPage)

      if (
        isGoingBackward ||
        (alreadyHasMarker && newPage > 0) ||
        cacheHasEnoughData ||
        cacheHasAllData
      ) {
        return
      }

      // Only show loading for forward navigation when fetching new data
      setIsPaginationLoading(true)

      try {
        // Determine which marker to use
        let requestMarker: string | undefined

        if (newPage > 0 && newPage > currentPage) {
          requestMarker = marker || pageMarkers[newPage] || undefined
          if (marker && !pageMarkers[newPage]) {
            setPageMarkers((prev) => {
              const updated = [...prev]
              updated[newPage] = marker
              return updated
            })
          }
        } else {
          requestMarker = undefined
        }

        const params = buildParams({
          marker: requestMarker,
          ...(searchParams && { searchParams }),
        })

        if (isSearchActive && searchParams) {
          const searchAction = getSearchAction()
          await searchAction.call(params)
        } else {
          const nextPageAction = getNextPageAction()
          await nextPageAction.call(params)
        }

        setIsPaginationLoading(false)
      } catch (error) {
        setIsPaginationLoading(false)
      }
    },
    [
      currentPage,
      pageMarkers,
      selectByContext,
      allData,
      selfData,
      data,
      pageSize,
      totalCount,
      buildParams,
      searchParams,
      isSearchActive,
      getSearchAction,
      getNextPageAction,
    ],
  )

  const handleRefresh = useCallback(async () => {
    // Reset pagination
    setCurrentPage(0)
    setPageMarkers([null])
    setIsPaginationLoading(true)

    try {
      const { reloadStatewise, reloadLite } = getReloadActions()

      // Always use explicit search/nextPage actions with params to preserve search state
      const params = buildParams({
        marker: null,
        ...(searchParams && { searchParams }),
      })

      const promises = []
      // Reload statewise and lite data to ensure counts are up to date
      promises.push(reloadStatewise(true))
      promises.push(reloadLite(true))

      if (isSearchActive && searchParams) {
        const searchAction = getSearchAction()
        promises.push(searchAction.call(params))
      } else {
        // Use nextPage action even for refresh to include sort/pagination params
        const nextPageAction = getNextPageAction()
        promises.push(nextPageAction.call(params))
      }

      await Promise.all(promises)
    } finally {
      setIsPaginationLoading(false)
    }
  }, [
    isSearchActive,
    searchParams,
    buildParams,
    getSearchAction,
    getNextPageAction,
    getReloadActions,
  ])

  const activePoll = useMemo(() => {
    // Build params for polling with current page context
    const pollParams = buildParams({
      marker: pageMarkers[currentPage],
      ...(searchParams && { searchParams }),
    })

    const pollAction = selectByContext(pollAllVms, pollSelfVms, pollVms)
    return () => pollAction(pollParams)
  }, [
    buildParams,
    pageMarkers,
    currentPage,
    searchParams,
    selectByContext,
    pollAllVms,
    pollSelfVms,
    pollVms,
  ])

  // Helper to get the appropriate dataKey based on user context
  const getDataKey = useCallback(
    () =>
      selectByContext(
        DataKeys.AllOpenstackVirtualMachines,
        DataKeys.SelfOpenstackVirtualMachines,
        DataKeys.OpenstackVirtualMachines,
      ),
    [selectByContext],
  )

  // Helper to update cached params in session after fetching data
  const updateCachedParamsInSession = useCallback(
    (params: { searchParams: any; sortKey: string; sortDir: string; pageSize: number }) => {
      const dataKey = getDataKey()
      const sessionKey =
        dataKey === DataKeys.AllOpenstackVirtualMachines
          ? sessionAllVMCachedParams
          : dataKey === DataKeys.SelfOpenstackVirtualMachines
          ? sessionSelfVMCachedParams
          : sessionVMCachedParams
      dispatch(sessionActions.updateSession({ [sessionKey]: params }))
    },
    [
      getDataKey,
      sessionAllVMCachedParams,
      sessionSelfVMCachedParams,
      sessionVMCachedParams,
      dispatch,
    ],
  )

  // Handle search - use lightweight search actions (no dependencies)
  const handleSearch = useCallback(
    async (field: string, value: string, filterParams: Record<string, string> = {}) => {
      // Combine search field + value with filter params
      const newSearchParams = value ? { [field]: value, ...filterParams } : { ...filterParams }
      setSearchParams(newSearchParams)
      setIsSearchActive(true)
      setCurrentPage(0)
      setPageMarkers([null])
      setIsPaginationLoading(true)

      // Save search params to session
      dispatch(sessionActions.updateSession({ [sessionSearchParams]: newSearchParams }))

      try {
        const params = buildParams({ searchParams: newSearchParams })
        updateCachedParamsInSession({ searchParams: newSearchParams, sortKey, sortDir, pageSize })

        const searchAction = getSearchAction()
        await searchAction.call(params)
      } finally {
        setIsPaginationLoading(false)
      }
    },
    [
      dispatch,
      sessionSearchParams,
      buildParams,
      updateCachedParamsInSession,
      sortKey,
      sortDir,
      pageSize,
      getSearchAction,
    ],
  )

  // Handle clear search - use lightweight actions to refetch without dependencies
  const handleClearSearch = useCallback(async () => {
    setSearchParams(host ? { host } : null)
    setIsSearchActive(!!host)
    setCurrentPage(0)
    setPageMarkers([null])
    setIsPaginationLoading(true)

    // Clear search-specific pagination data from session
    const cachedParamsKey = selectByContext(
      sessionAllVMCachedParams,
      sessionSelfVMCachedParams,
      sessionVMCachedParams,
    )
    dispatch(
      sessionActions.updateSession({
        [cachedParamsKey]: undefined,
        [sessionSearchParams]: null,
      }),
    )

    try {
      const params = buildParams({})
      updateCachedParamsInSession({ searchParams: null, sortKey, sortDir, pageSize })

      if (isSearchActive && searchParams) {
        const searchAction = getSearchAction()
        await searchAction.call(params)
      } else {
        // Use nextPage action even for refresh to include sort/pagination params
        const nextPageAction = getNextPageAction()
        await nextPageAction.call(params)
      }
    } finally {
      setIsPaginationLoading(false)
    }
  }, [
    selectByContext,
    sessionAllVMCachedParams,
    sessionSelfVMCachedParams,
    sessionVMCachedParams,
    sessionSearchParams,
    dispatch,
    buildParams,
    updateCachedParamsInSession,
    sortKey,
    sortDir,
    pageSize,
    getNextPageAction,
  ])

  // Convert API format searchParams to VMFilterParams format for UI
  // API format: user_id -> VMFilterParams format: owner
  const filterParamsForUI = useMemo((): VMFilterParams => {
    if (!searchParams) {
      return defaultSearchParams
    }
    return {
      availability_zone: searchParams.availability_zone || 'all',
      vm_state: searchParams.vm_state || 'all',
      user_id: searchParams.user_id || 'all',
      flavor: searchParams.flavor || 'all',
      image: searchParams.image || 'all',
      host: searchParams.host || 'all',
      project_id: searchParams.project_id || '',
    }
  }, [searchParams])

  // Handle sort - reload data with new sort parameters
  const handleSort = useCallback(
    async (newSortKey: string, newSortDir: 'asc' | 'desc') => {
      setSortKey(newSortKey)
      setSortDir(newSortDir)
      setCurrentPage(0)
      setPageMarkers([null])
      setIsPaginationLoading(true)

      // Save sort key and direction to session using dynamic keys
      dispatch(
        sessionActions.updateSession({
          [sessionSortKey]: newSortKey,
          [sessionSortDir]: newSortDir,
        }),
      )

      // Read current session to get latest search params
      const currentSession = store.getState().session
      const currentSearchParams = currentSession?.[sessionSearchParams]

      try {
        const params = buildParams({
          sortKey: newSortKey,
          sortDir: newSortDir,
          ...(currentSearchParams && { searchParams: currentSearchParams }),
        })

        // Use search or nextPage action based on whether search is active
        const action = currentSearchParams ? getSearchAction() : getNextPageAction()
        await action.call(params)
      } finally {
        setIsPaginationLoading(false)
      }
    },
    [
      dispatch,
      sessionSortKey,
      sessionSortDir,
      sessionSearchParams,
      buildParams,
      getSearchAction,
      getNextPageAction,
    ],
  )

  // Handle page size change - reload data with new page size
  const handlePageSizeChange = useCallback(
    async (newPageSize: number) => {
      setPageSize(newPageSize)
      setCurrentPage(0)
      setPageMarkers([null])

      // Persist page size preference to session
      dispatch(sessionActions.updateSession({ [sessionPageSize]: newPageSize }))

      // Check if cache already has enough data for the new page size
      const currentCachedData = selectByContext(allData, selfData, data)
      const cacheLength = currentCachedData?.length || 0
      const cacheHasEnoughData = cacheLength >= newPageSize
      const cacheHasAllData = totalCount != null && cacheLength >= totalCount

      // Skip API call if cache has sufficient data
      if (cacheHasEnoughData || cacheHasAllData) {
        return
      }

      setIsPaginationLoading(true)

      try {
        const params = buildParams({ limit: newPageSize })
        const action = isSearchActive && searchParams ? getSearchAction() : getNextPageAction()
        await action.call(params)
      } finally {
        setIsPaginationLoading(false)
      }
    },
    [
      dispatch,
      sessionPageSize,
      selectByContext,
      allData,
      selfData,
      data,
      totalCount,
      buildParams,
      isSearchActive,
      searchParams,
      getSearchAction,
      getNextPageAction,
    ],
  )

  // ========== Effects ==========
  // Load data when toggling between allTenants/allOwners
  useEffect(() => {
    // Skip on initial mount
    if (isInitialMount.current) {
      isInitialMount.current = false
      return
    }

    // Reset pagination when context changes
    setCurrentPage(0)
    setPageMarkers([null])
    loadVMsData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allTenants, allOwners])

  // Initial data load
  useEffect(() => {
    if (hasInitialDataLoaded.current) {
      return
    }
    hasInitialDataLoaded.current = true

    // Check if data exists for current view
    const currentData = selectByContext(allData, selfData, data)
    const hasData = currentData && currentData.length > 0

    // Check if we should reload based on last visited page
    const isFirstVisit = session?.lastVisitedVmPage == null
    const needsReload =
      sessionKey === 'vmList'
        ? session?.lastVisitedVmPage === 'hostList'
        : session?.lastVisitedVmPage === 'vmList'

    const hostChanged =
      session?.lastVisitedVmPage === 'hostList' && host !== cachedSearchParams?.host

    const shouldReload = !hasData || isFirstVisit || needsReload || hostChanged

    // Update session to mark this page as last visited
    dispatch(sessionActions.updateSession({ lastVisitedVmPage: sessionKey }))

    if (!shouldReload) {
      return
    }

    // Update search params if host changed
    if (hostChanged) {
      dispatch(
        sessionActions.updateSession({
          [sessionSearchParams]: searchParams,
        }),
      )
    }

    loadVMsData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // ========== Loading States ==========
  const isStatewiseVMsLoading = useMemo(
    () => selectByContext(statewiseLoading, allStatewiseLoading, selfStatewiseLoading),
    [selectByContext, statewiseLoading, allStatewiseLoading, selfStatewiseLoading],
  )

  // Return all the data and functions that components need
  return {
    // Data
    data: filteredData,
    paginatedVMs,
    totalCount,

    // Loading states
    loading: activeLoading,
    polling: activePolling,
    refreshing: activeRefreshing,
    message: activeMessage,

    // Pagination
    currentPage,
    pageSize,
    pageMarkers,
    hasNextPage,
    isPaginationLoading,
    handlePageSizeChange,

    // Actions
    reload: handleRefresh,
    poll: activePoll,
    handlePageChange,
    handleRefresh,
    setCurrentPage,
    setPageSize,
    setPageMarkers,
    setSearchParams,
    setIsSearchActive,

    // Search
    searchParams,
    isSearchActive,
    handleSearch,
    handleClearSearch,
    filterParamsForUI,

    // Sort
    sortKey,
    sortDir,
    handleSort,

    // Statewise counts
    vmStateCount: selectByContext(allVmStateCount, selfVmStateCount, vmStateCount),
    isStatewiseVMsLoading,

    // Lite data for UI
    liteData: selectByContext(allTenantsLiteData, selfLiteData, regularLiteData),
  }
}
